/**
 * SOLID Review
 * S — Strong. One concern: the frontend Domain's lollipop, as a runtime contract — every decision it
 *     offers, with the schema of what goes in and what comes out.
 * O — Good. A new decision is a new row; the contract, the type and the committed document follow from
 *     it. Not Strong because every row lives in one file, by design: the lollipop is one seam.
 * L — N/A.
 * I — Strong. One contract, one derived type.
 * D — Good. The kernel's zod adapter and the Domain's own schemas. The Domain owns this file (rule 1:
 *     the layer that PROVIDES a lollipop defines it); the Application imports the object.
 */
import { z } from 'zod';
import { zodPortContract, type PortInterface, type IConfigZodPortContract } from '@ml3d/kernel/zod';
import { ContextRequest } from './schemas/ContextRequest';
import { ResultOf } from './schemas/ResultSchema';
import { DecisionOf } from './schemas/DecisionOutput';
import { ClientTableInput } from './schemas/ClientTable';
import { ClientChanges, ClientInput } from './schemas/ClientChanges';
import {
  PayloadRoundStarted, PayloadClaimAnnounced, PayloadCupLifted, PayloadDiceRolled, PayloadRoomRoster, PayloadPresence,
  PayloadRoomAbandoned, PayloadDecisionCountdown, PayloadTableView, PayloadConnectivity,
  DtoTableAck, DtoTableView, DtoConnectivity,
} from './schemas/TableEvents';
import {
  CommandNothing, CommandAnnounce, CommandLeave, CommandGameStarted, CommandRoundMoveAnswered, CommandPeeked,
  CommandClaimAccepted, CommandBelieveAccepted, CommandPlayAgainAnswered, CommandActiveRoundRestored,
  CommandSuspendedReconciled, CommandLobbyActiveRound, CommandLobbyRoomLoaded,
  RequestStartGame, RequestRoundMove, RequestAnnounce, RequestBelieve, RequestPlayAgain, RequestResync, RequestRoomRead,
  DtoLeave,
} from './schemas/TableMoves';
import {
  CommandSessionStarted, CommandPlanEntry, CommandMeAnswered, CommandResumeValidated, CommandPlanRestore, CommandRestoreJoined,
  CommandRoundRestored, CommandPlanIntent, CommandJoinRouted, CommandTableJoined, CommandTableRejoined, CommandTableLateJoined,
  CommandTableHosted, CommandActiveRoomFound, CommandCreateGuest, CommandGuestCreated, CommandTableCode, CommandTableCodeChecked,
  CommandNavigate, RequestSeat, RequestCodeLookup, RequestHost, RequestPlayer, RequestGuest,
  DtoEntryPlan, DtoSignOut, DtoProceed, DtoRoundRead, DtoIntent, DtoJoinRoute, DtoColdRoom,
} from './schemas/SessionMoves';
import {
  CommandEmail, CommandLoginCodeAnswered, CommandSignUp, CommandSignedUp, CommandVerifyCode, CommandSignedIn, CommandProfileLoaded,
  CommandNickname, CommandEmailCode, CommandEmailChanged, RequestEmail, RequestSignUp, RequestVerifyCode, RequestNickname,
  RequestEmailCode,
} from './schemas/AuthMoves';
import {
  CommandPlanLegalReview, DtoPlanLegalReview, CommandLegalReviewOf, DtoLegalReview, CommandLegalAcceptance, DtoLegalAcceptance,
} from './schemas/LegalMoves';
import { CommandConsentChanged, DtoConsentRecord, CommandCookieConsentRecord, DtoCookieConsentRecord } from './schemas/ConsentMoves';
import { CommandCreditsChanged, CommandBalanceRead, PayloadAccountWatch } from './schemas/PaymentMoves';
import { DtoAdConfigPlan, CommandAdConfigLoaded } from './schemas/AdsMoves';
import { CommandMediaClock, CommandMediaIdle, CommandMediaBanner, CommandMediaUsed, DtoPresencePlan } from './schemas/MediaMoves';
import { CommandClientError, DtoClientErrorPlan } from './schemas/DiagnosticsMoves';
import { CommandDiceRankingLoaded } from './schemas/DiceRankingMoves';

/**
 * THE FRONTEND DOMAIN'S ONE LOLLIPOP (frontend strict-ports plan, FE1, 2026-10-03) — the client twin of
 * `DomainDecisions`.
 *
 * Every op is `(ctx, { table }, command) → Result<{ changes, dto }>`. The table is the client's whole
 * table, as `ClientState.snapshot` returns it; `changes` is what `ClientState.apply` saves, with the
 * derived values for the screen; `dto` answers the shell — for a `plan*` op, the request to send. Nothing
 * that crosses this port is a class, a Date or a function, which is what lets the Domain run on another
 * processor with exactly one message per decision (FE8's proof).
 *
 * Declared async so the port is uniform with every other non-local port and remoteable; the
 * implementation does no I/O, which the decision tests verify by calling it with nothing wired.
 *
 * The table grows one context at a time. FE1 is the table loop: the server's events, the reconnect's
 * catch-up, the lobby's reads, and the local player's moves, each a plan before its server call and an
 * answer after it. FE2 is the session: a session op reads `{ table, session }` and may change both. FE3
 * is the account: signing in and up, the profile, signing out — session ops too. Since frontend-domain-oo
 * P2 a table op also reads the rules the table is played by — `{ table, diceRanking }`, the session's
 * ranking of the dice, as the server served it — and one session op keeps them (`diceRankingLoaded`).
 */
function tableOp<C extends z.ZodTypeAny, D extends z.ZodTypeAny>(command: C, dto: D) {
  return {
    args: z.tuple([ContextRequest, ClientTableInput, command]),
    result: ResultOf(DecisionOf(ClientChanges, dto)),
  };
}

function sessionOp<C extends z.ZodTypeAny, D extends z.ZodTypeAny>(command: C, dto: D) {
  return {
    args: z.tuple([ContextRequest, ClientInput, command]),
    result: ResultOf(DecisionOf(ClientChanges, dto)),
  };
}

const ClientDecisionsOps = {
  // ── the server's events ───────────────────────────────────────────────────────────────────────
  roundStarted: tableOp(PayloadRoundStarted, DtoTableAck),
  claimAnnounced: tableOp(PayloadClaimAnnounced, DtoTableAck),
  cupLifted: tableOp(PayloadCupLifted, DtoTableAck),
  diceRolled: tableOp(PayloadDiceRolled, DtoTableAck),
  roomUpdated: tableOp(PayloadRoomRoster, DtoTableAck),
  playerSuspended: tableOp(PayloadPresence, DtoTableAck),
  playerResumed: tableOp(PayloadPresence, DtoTableAck),
  roomAbandoned: tableOp(PayloadRoomAbandoned, DtoTableAck),
  decisionCountdown: tableOp(PayloadDecisionCountdown, DtoTableAck),
  tableView: tableOp(PayloadTableView, DtoTableView),
  connectivity: tableOp(PayloadConnectivity, DtoConnectivity),
  // ── catching up: a reconnect's resync and the lobby's reads ───────────────────────────────────
  planResync: tableOp(CommandNothing, RequestResync),
  activeRoundRestored: tableOp(CommandActiveRoundRestored, DtoTableAck),
  suspendedReconciled: tableOp(CommandSuspendedReconciled, DtoTableAck),
  planLobbyRead: tableOp(CommandNothing, RequestRoomRead),
  lobbyActiveRound: tableOp(CommandLobbyActiveRound, DtoTableAck),
  lobbyRoomLoaded: tableOp(CommandLobbyRoomLoaded, DtoTableAck),
  // ── the local player's moves: a plan before the server call, an answer after it ───────────────
  planStartGame: tableOp(CommandNothing, RequestStartGame),
  gameStarted: tableOp(CommandGameStarted, DtoTableAck),
  planRoll: tableOp(CommandNothing, RequestRoundMove),
  rolled: tableOp(CommandRoundMoveAnswered, DtoTableAck),
  planPeek: tableOp(CommandNothing, RequestRoundMove),
  peeked: tableOp(CommandPeeked, DtoTableAck),
  planAnnounce: tableOp(CommandAnnounce, RequestAnnounce),
  claimAccepted: tableOp(CommandClaimAccepted, DtoTableAck),
  planBelieve: tableOp(CommandNothing, RequestBelieve),
  believeAccepted: tableOp(CommandBelieveAccepted, DtoTableAck),
  planRollFirst: tableOp(CommandNothing, RequestBelieve),
  rollFirstAccepted: tableOp(CommandBelieveAccepted, DtoTableAck),
  planLift: tableOp(CommandNothing, RequestRoundMove),
  liftAnswered: tableOp(CommandRoundMoveAnswered, DtoTableAck),
  planPlayAgain: tableOp(CommandNothing, RequestPlayAgain),
  playAgainAnswered: tableOp(CommandPlayAgainAnswered, DtoTableAck),
  leaveTable: tableOp(CommandLeave, DtoLeave),
  // ── the session (FE2): starting, entering the game screen, identity, the stored game ─────────
  sessionStarted: sessionOp(CommandSessionStarted, DtoTableAck),
  planEntry: sessionOp(CommandPlanEntry, DtoEntryPlan),
  meAnswered: sessionOp(CommandMeAnswered, DtoSignOut),
  meUnreachable: sessionOp(CommandNothing, DtoTableAck),
  resumeValidated: sessionOp(CommandResumeValidated, DtoProceed),
  planRestore: sessionOp(CommandPlanRestore, RequestSeat),
  restoreJoined: sessionOp(CommandRestoreJoined, DtoRoundRead),
  roundRestored: sessionOp(CommandRoundRestored, DtoTableAck),
  planIntent: sessionOp(CommandPlanIntent, DtoIntent),
  entryFinished: sessionOp(CommandNothing, DtoTableAck),
  // ── taking a seat: by code, by hosting, by the server's word ──────────────────────────────────
  planJoin: sessionOp(CommandNothing, RequestCodeLookup),
  joinRouted: sessionOp(CommandJoinRouted, DtoJoinRoute),
  tableJoined: sessionOp(CommandTableJoined, DtoProceed),
  tableRejoined: sessionOp(CommandTableRejoined, DtoRoundRead),
  tableLateJoined: sessionOp(CommandTableLateJoined, DtoRoundRead),
  planHost: sessionOp(CommandNothing, RequestHost),
  tableHosted: sessionOp(CommandTableHosted, DtoProceed),
  planColdRejoin: sessionOp(CommandNothing, RequestPlayer),
  activeRoomFound: sessionOp(CommandActiveRoomFound, DtoColdRoom),
  // ── the entry funnel ──────────────────────────────────────────────────────────────────────────
  planGuest: sessionOp(CommandCreateGuest, RequestGuest),
  guestCreated: sessionOp(CommandGuestCreated, DtoTableAck),
  planTableCode: sessionOp(CommandTableCode, RequestCodeLookup),
  tableCodeChecked: sessionOp(CommandTableCodeChecked, DtoTableAck),
  navigate: sessionOp(CommandNavigate, DtoTableAck),
  dismissJoinError: sessionOp(CommandNothing, DtoTableAck),
  // ── the account (FE3): signing in by code, signing up in place, the profile, signing out ──────
  planLoginCode: sessionOp(CommandEmail, RequestEmail),
  loginCodeAnswered: sessionOp(CommandLoginCodeAnswered, DtoTableAck),
  planSignUp: sessionOp(CommandSignUp, RequestSignUp),
  signedUp: sessionOp(CommandSignedUp, DtoTableAck),
  planVerifyCode: sessionOp(CommandVerifyCode, RequestVerifyCode),
  signedIn: sessionOp(CommandSignedIn, DtoTableAck),
  profileLoaded: sessionOp(CommandProfileLoaded, DtoTableAck),
  planNickname: sessionOp(CommandNickname, RequestNickname),
  nicknameChanged: sessionOp(CommandNickname, DtoTableAck),
  planEmailChange: sessionOp(CommandEmail, RequestEmail),
  planVerifyEmailChange: sessionOp(CommandEmailCode, RequestEmailCode),
  emailChanged: sessionOp(CommandEmailChanged, DtoTableAck),
  signedOut: sessionOp(CommandNothing, DtoTableAck),
  // ── the legal side (FE4a): the re-acceptance gate's rules ─────────────────────────────────────
  planLegalReview: sessionOp(CommandPlanLegalReview, DtoPlanLegalReview),
  legalReviewOf: sessionOp(CommandLegalReviewOf, DtoLegalReview),
  planLegalAcceptance: sessionOp(CommandLegalAcceptance, DtoLegalAcceptance),
  // ── cookie consent (FE4b) ─────────────────────────────────────────────────────────────────────
  consentChanged: sessionOp(CommandConsentChanged, DtoConsentRecord),
  planCookieConsentRecord: sessionOp(CommandCookieConsentRecord, DtoCookieConsentRecord),
  // ── the account's credits (FE4c) ──────────────────────────────────────────────────────────────
  creditsChanged: sessionOp(CommandCreditsChanged, DtoTableAck),
  balanceRead: sessionOp(CommandBalanceRead, DtoTableAck),
  planAccountWatch: sessionOp(CommandNothing, PayloadAccountWatch),
  // ── the ad slot (FE4d) ────────────────────────────────────────────────────────────────────────
  planAdConfig: sessionOp(CommandNothing, DtoAdConfigPlan),
  adConfigLoaded: sessionOp(CommandAdConfigLoaded, DtoTableAck),
  // ── the table's video chat (FE5c) ─────────────────────────────────────────────────────────────
  mediaToggled: sessionOp(CommandMediaClock, DtoTableAck),
  mediaReconnect: sessionOp(CommandMediaClock, DtoTableAck),
  mediaIdleChecked: sessionOp(CommandMediaIdle, DtoTableAck),
  mediaRemovedByServer: sessionOp(CommandNothing, DtoTableAck),
  mediaBannerDismissed: sessionOp(CommandMediaBanner, DtoTableAck),
  planPresence: sessionOp(CommandMediaUsed, DtoPresencePlan),
  // ── client error reporting (2026-10-10) ────────────────────────────────────────────────────────
  planClientError: sessionOp(CommandClientError, DtoClientErrorPlan),
  // ── the rules the table is played by (frontend-domain-oo P2): the server's dice ranking, kept ──
  diceRankingLoaded: sessionOp(CommandDiceRankingLoaded, DtoTableAck),
} as const;

const clientDecisionsConfig: IConfigZodPortContract<typeof ClientDecisionsOps> = { name: 'ClientDecisions', ops: ClientDecisionsOps };
export const ClientDecisionsContract = zodPortContract(clientDecisionsConfig);

export type IPortClientDecisions = PortInterface<typeof ClientDecisionsOps>;
